import api from './axiosApi'
import type { PlanItem } from './planItems'

// Frontend payload type (used by wizard)
export interface CreatePlanPayload {
  method: 'blank' | 'copy' | 'historicalData' | 'planogram'
  planName: string
  // Blank flow
  departmentId?: string
  classId?: string
  hierarchyId?: string
  // Copy flow
  sourcePlanId?: string
  sourceVersionId?: string
  // Historical data flow
  historicalSourceType?: 'plan' | 'file'
  historicalSourcePlanId?: string
  uploadedFileName?: string
  historicalDataItems?: PlanItem[]
  productListId?: string
}

// Backend API request type (matches OpenAPI PlanCreate schema)
interface PlanCreateRequest {
  name: string
  plan_type: 'CORE' | 'SEASONAL'
  description?: string | null
  season_id?: string | null
  fiscal_year?: number | null
  hierarchy_id?: string | null
  created_from: 'BLANK' | 'COPY' | 'PRODUCT_LIST'
  plan_group_id?: string | null
  source_version_id?: string | null
  product_list_id?: string | null
  /** A plan copy: the source's header and MFPs come too (not for historical data). */
  copy_header?: boolean
}

// Backend API response type (matches OpenAPI PlanRead schema)
export interface PlanReadResponse {
  id: string
  name: string
  plan_type: 'CORE' | 'SEASONAL'
  status: string
  season_id?: string | null
  fiscal_year?: number | null
  hierarchy_id?: string | null
  created_at: string
  modified_at?: string | null
  created_from: 'BLANK' | 'COPY' | 'PRODUCT_LIST'
}

// Frontend response type (transformed for wizard)
export interface CreatePlanResponse {
  id: string
  name: string
  createdAt: string
}

/**
 * Map wizard payload to backend created_from enum.
 * Historical flow has two sub-paths:
 * - plan source behaves like copy
 * - file source behaves like product list import
 */
function mapPayloadToCreatedFrom(
  payload: CreatePlanPayload
): 'BLANK' | 'COPY' | 'PRODUCT_LIST' {
  if (payload.method === 'historicalData') {
    return payload.historicalSourceType === 'plan' ? 'COPY' : 'PRODUCT_LIST'
  }

  if (payload.method === 'planogram') {
    return 'PRODUCT_LIST'
  }

  return payload.method === 'copy' ? 'COPY' : 'BLANK'
}

/**
 * Determine the hierarchy_id to send to the API
 * Priority: subclass > class > department
 */
function resolveHierarchyId(payload: CreatePlanPayload): string | null {
  // For blank flow, use the most specific selection
  if (payload.hierarchyId) return payload.hierarchyId
  if (payload.classId) return payload.classId
  if (payload.departmentId) return payload.departmentId
  return null
}

/**
 * Create a new plan via the API
 */
export async function createPlan(
  payload: CreatePlanPayload
): Promise<CreatePlanResponse> {
  const requestBody: PlanCreateRequest = {
    name: payload.planName.trim(),
    plan_type: 'CORE', // All plans are now Core
    created_from: mapPayloadToCreatedFrom(payload),
  }

  // Add hierarchy_id based on method
  if (payload.method === 'blank') {
    requestBody.hierarchy_id = resolveHierarchyId(payload)
  }

  // For copy/historicalData flows, the backend may handle hierarchy differently
  // Currently, we pass hierarchyId if available
  if (payload.method !== 'blank' && payload.hierarchyId) {
    requestBody.hierarchy_id = payload.hierarchyId
  }

  // Add source_version_id for copy flow; a copy also takes the source's header
  if (payload.method === 'copy' && payload.sourceVersionId) {
    requestBody.source_version_id = payload.sourceVersionId
    requestBody.copy_header = true
  }

  // Add product_list_id for historical data with file source
  if (
    payload.method === 'historicalData' &&
    payload.historicalSourceType === 'file' &&
    payload.productListId
  ) {
    requestBody.product_list_id = payload.productListId
  }

  // Add product_list_id for planogram flow
  if (payload.method === 'planogram' && payload.productListId) {
    requestBody.product_list_id = payload.productListId
  }

  // Add source_version_id for historical data with plan source
  if (
    payload.method === 'historicalData' &&
    payload.historicalSourceType === 'plan' &&
    payload.sourceVersionId
  ) {
    requestBody.source_version_id = payload.sourceVersionId
  }

  const response = await api.post<PlanReadResponse>('/plans', requestBody)

  // Transform backend response to frontend format
  return {
    id: response.data.id,
    name: response.data.name,
    createdAt: response.data.created_at,
  }
}
